import type { SuggestionData } from 'service/search/model';
import {
	clearSuggestions,
	handleClickOutsideOfSearchBlock,
	handleSubmit,
	removeSavedSearchId,
	searchCategorySeoName,
	searchQueryFromSuggest,
	suggestsData,
	suggestsDataLocal,
} from './signals';
import clsx from 'clsx';
import { useEffect } from 'preact/hooks';
import { Signal } from '@preact/signals';
import { localize } from 'i18n/localize';
import { filters } from 'signals/filters';
import { Icon } from 'components/atoms/Icon.tsx';
import { CATEGORY_ALL_CATEGORIES } from 'service/category/model.ts';
import { cn } from 'utils/cn';
import { useIsDesktopAndLarger } from 'signals/mediaQuery';

export const HEADER_SEARCH_SUGGESTION_ID = 'search-suggestion';
const $t = localize();

type Props = {
	className?: string;
	focusedIndex: number;
	isFromHeader?: boolean;
	isSearchBoxFocused: Signal<boolean>;
	showSuggests: Signal<boolean>;
	searchBlockId: string;
	searchLocationId: string;
};
export function Suggestions({
	className,
	focusedIndex,
	isFromHeader,
	isSearchBoxFocused,
	searchBlockId,
	searchLocationId,
	showSuggests,
}: Props) {
	useEffect(() => {
		const onClick = (event: MouseEvent) => {
			handleClickOutsideOfSearchBlock(event, searchBlockId, searchLocationId, showSuggests);
		};
		globalThis.window.addEventListener('click', onClick);

		return () => globalThis.window.removeEventListener('click', onClick);
	}, []);

	const dataLength = suggestsData.value.length;

	if (!dataLength) {
		return null;
	}

	return (
		<div
			class={cn(
				'bg-neutral-white w-full lg:w-[calc(100%_-_60px)] mt-2 md:mt-1 md:rounded-3xl border border-[#d1d1d1] md:border-solid md:shadow-lg md:absolute overflow-hidden',
				'pb-20 md:pb-0', // Add padding to the suggestions box on phone to make scrolling space for the fixed search button at the bottom
				className,
			)}
			id="header-suggestions"
			role="listbox"
		>
			<ul class="md:[&>li:first-child>div]:pt-4 md:[&>li:last-child>div]:pb-4">
				{suggestsDataLocal.value && <LastSearchesLabel />}
				{suggestsData.value.map((suggest, index) => (
					<li key={index}>
						<Suggestion
							suggest={suggest}
							isFromHeader={isFromHeader}
							isFocused={index === focusedIndex}
							index={index}
							isSearchBoxFocused={isSearchBoxFocused}
							showSuggests={showSuggests}
						/>
					</li>
				))}
			</ul>
		</div>
	);
}

export function LastSearchesLabel() {
	return (
		<li>
			<div class={clsx('py-2 px-4 text-xs font-bold text-light-blue')}>
				<span>{$t.searchBox.lastSearches}</span>
			</div>
		</li>
	);
}

export function Suggestion({
	suggest: { sentence, categoryPath, savedSearchId },
	isFocused,
	isFromHeader,
	index,
	isSearchBoxFocused,
	showSuggests,
}: {
	suggest: SuggestionData;
	isFocused: boolean;
	isFromHeader?: boolean;
	index: number;
	isSearchBoxFocused: Signal<boolean>;
	showSuggests: Signal<boolean>;
}) {
	const { searchBox } = localize();
	const isDesktop = useIsDesktopAndLarger();

	return (
		<button
			class={clsx('cursor-pointer suggestion w-full text-left', { 'bg-neutral-10': isFocused })}
			id={`${HEADER_SEARCH_SUGGESTION_ID}-${index}`}
			aria-selected={isFocused}
			aria-label={sentence}
			tabIndex={-1}
			role="option"
			type="button"
			onClick={(event) => {
				filters.value = filters.getUpdatedValue({ hledej: { seoName: sentence } });
				searchCategorySeoName.value = categoryPath?.at(-1) ?? CATEGORY_ALL_CATEGORIES;
				searchQueryFromSuggest.value = true;

				if (isFromHeader || isDesktop.peek()) {
					handleSubmit(event, isSearchBoxFocused, showSuggests, true);
				} else {
					clearSuggestions();
				}
			}}
		>
			<div
				class={clsx(
					'py-2.5 px-5 hover:bg-neutral-10 flex',
					savedSearchId !== undefined ? 'justify-between' : null,
				)}
			>
				<span>
					<span class="text-sm flex md:inline-block items-center font-medium">
						{suggestsDataLocal.value && <Icon type="search" size={16} className="mr-2 md:hidden" />}{' '}
						{sentence}
					</span>
					{categoryPath?.map(({ name, id }, index) => (
						<span class="text-xs font-medium text-dark-blue-50 [&>span]:[&:nth-child(2)]:hidden md:[&>span]:[&:nth-child(2)]:inline-block">
							<span class="px-1">{'>'}</span>
							{id === 0 ? (
								$t.searchBox.inAllCategories
							) : (
								<>
									{index === 0 && searchBox.inWords}
									{name}
								</>
							)}
						</span>
					))}
				</span>
				{savedSearchId !== undefined && (
					<button
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							removeSavedSearchId(savedSearchId);
						}}
						class="leading-0 self-center suggestion"
						title={searchBox.deleteLastSearch}
					>
						<Icon type="close" size={16} className="text-dark-blue-50 self-center" />
					</button>
				)}
			</div>
		</button>
	);
}
